export interface RoutedResourceConfig {
  /**
   * Read when a method is called rather than when the resource is built, so the
   * resource can be created once and still follow a flag that resolves later.
   */
  isV2: () => boolean;
  /** Rewrites a `createChildApi` action from the Java path to the Node path. */
  remapAction: (action: string) => string;
  /** Actions with no Node counterpart, which stay on Java whatever the flag says. */
  isJavaOnlyAction?: (action?: string) => boolean;
}

interface ChildApiOptions {
  action?: string;
  [key: string]: unknown;
}

/**
 * Wraps a Java and a Node `genResource` pair in a single resource that picks between
 * them per call.
 *
 * The returned object is built once and never replaced, so consumers can hold it in
 * effect deps without the effect re-firing when the flag resolves — a flag change
 * takes effect on the next request instead of triggering a refetch against the other
 * backend. Every method the underlying resource exposes is forwarded, so a caller
 * reaching for `find` or `patch` gets the real thing rather than `undefined`.
 */
const makeRoutedResource = <T>(
  javaApi: T,
  nodeApi: T,
  config: RoutedResourceConfig
): T => {
  const pick = () => (config.isV2() ? nodeApi : javaApi);

  const routed: Record<string, unknown> = Object.fromEntries(
    Object.keys(javaApi as object).map(method => [
      method,
      (...args: unknown[]) =>
        (pick() as Record<string, (...a: unknown[]) => unknown>)[method](
          ...args
        )
    ])
  );

  const childOf = (api: T, options: ChildApiOptions) =>
    ((api as unknown) as {
      createChildApi: (o: ChildApiOptions) => T;
    }).createChildApi(options);

  routed.createChildApi = (options: ChildApiOptions = {}) => {
    if (config.isJavaOnlyAction?.(options.action)) {
      return childOf(javaApi, options);
    }

    return makeRoutedResource(
      childOf(javaApi, options),
      childOf(
        nodeApi,
        options.action
          ? { ...options, action: config.remapAction(options.action) }
          : options
      ),
      config
    );
  };

  // Forwarders are built from the resource's own method names, so the shape matches
  // by construction and cannot be proven structurally.
  return (routed as unknown) as T;
};

export default makeRoutedResource;
